import { useEffect, useRef, useState } from 'react'

interface Option {
    label: string
    value: string
}

interface Props {
    label?: string
    options: Option[]
    value: string[]
    onChange: (value: string[]) => void
    error?: string
    placeholder?: string
}

const MultiSelect = ({
    label,
    options,
    value,
    onChange,
    error,
    placeholder = 'Search widget...',
}: Props) => {
    const [search, setSearch] = useState('')
    const [open, setOpen] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    /* Close dropdown on outside click */
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target as Node)
            ) {
                setOpen(false)
            }
        }
        document.addEventListener('click', handler)
        return () => document.removeEventListener('click', handler)
    }, [])

    const filteredOptions = options.filter(
        (opt) =>
            opt.label.toLowerCase().includes(search.toLowerCase()) &&
            !value.includes(opt.value)
    )

    const addItem = (val: string) => {
        onChange([...value, val])
        setSearch('')
        setOpen(false)
    }

    const removeItem = (val: string) => {
        onChange(value.filter((v) => v !== val))
    }

    return (
        <div ref={wrapperRef} className="relative">
            {label && (
                <label className="block text-sm font-medium mb-1">
                    {label}
                </label>
            )}

            {/* Search input */}
            <input
                type="text"
                value={search}
                onFocus={() => setOpen(true)}
                onChange={(e) => {
                    setSearch(e.target.value)
                    setOpen(true)
                }}
                placeholder={placeholder}
                className={`w-full border rounded px-3 py-2 text-sm ${error ? 'border-red-500' : 'border-slate-300'
                    }`}
            />

            {/* Dropdown */}
            {open && (
                <div className="absolute z-50 w-full mt-1 max-h-48 overflow-auto rounded border bg-white dark:bg-slate-800 shadow">
                    {options.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-slate-500">
                            Loading widgets…
                        </div>
                    ) : filteredOptions.length ? (
                        filteredOptions.map((opt) => (
                            <div
                                key={opt.value}
                                onClick={() => addItem(opt.value)}
                                className="px-3 py-2 text-sm cursor-pointer hover:bg-slate-100"
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="px-3 py-2 text-xs text-slate-500">
                            No widgets found
                        </div>
                    )}

                </div>
            )}

            {/* Selected chips */}
            <div className="flex flex-wrap gap-2 mt-2">
                {value.map((val) => (
                    <span
                        key={val}
                        className="flex items-center gap-2 px-2 py-1 text-xs rounded bg-slate-100 dark:bg-slate-700"
                    >
                        {val}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()   // 🔥 KEY FIX
                                removeItem(val)
                            }}
                            className="text-red-500 hover:text-red-700"
                        >
                            ✕
                        </button>

                    </span>
                ))}
            </div>

            {error && (
                <p className="text-xs text-red-500 mt-1">{error}</p>
            )}
        </div>
    )
}

export default MultiSelect
